import {
	ChangeEvent,
	ComponentProps,
	FocusEvent,
	ReactNode,
	forwardRef,
	useCallback,
	useRef,
	useState,
} from "react";
import { useField } from "formik";
import classNames from "classnames";
import { InputCore } from "app/components/form/InputCore/InputCore";
import { NativeSelect } from "app/components/form/NativeSelect/NativeSelect";
import { FieldLabel } from "app/components/form/Field/Field";
import { FieldHint } from "app/components/form/FieldHint/FieldHint";
import { makeStableClassName } from "app/components/form/utils/stableClassName";
import HorizontalDotLoader from "app/pages/.shared/HorizontalDotLoader";
import ArrowDownIcon from "app/icons/navigation/ArrowDownIcon";
import { ICON_SIZES } from "app/constants";
import styles from "./FormikSelectField.module.scss";

type FormikSelectFieldProps = {
	id: string;
	name: string;
	label: ReactNode;
	isRequired?: boolean;
	disabled?: boolean;
	loading?: boolean;
	hint?: string;
	className?: string;
	"data-testid"?: string;
	children: ReactNode;
} & Omit<ComponentProps<"select">, "id" | "name" | "className" | "children" | "ref" | "value">;

// Layer 2 adapter: same public contract/behavior as legacy SelectFormik (see
// SelectFormik.test.jsx), rendered with design-system primitives instead of hand-rolled markup.
// Unlike InputFormik/FormikTextField, never calls helpers.setTouched(true) on focus — preserved exactly.
const FormikSelectField = forwardRef<HTMLSelectElement, FormikSelectFieldProps>(
	(
		{
			id,
			name,
			isRequired,
			disabled,
			loading,
			label,
			hint,
			className,
			"data-testid": dataCy,
			children,
			onBlur,
			onChange,
			...restProps
		},
		ref
	) => {
		const [hasChanged, setHasChanged] = useState(false);
		const [field, meta, helpers] = useField(name);

		// Exact same rule as SelectFormik. See FormikTextField.tsx for why a "hide label when
		// disabled" Figma addition was reverted here too: it broke AddressAutocompleteFormik-
		// style consumers that pass disabled as a transient loading flag, not a permanent state.
		// No `meta.touched` here: a failed submit touches every field, which used to wrongly float an empty+invalid label (FQ-1143).
		const isLabelFloated = Boolean(field.value) || Boolean(loading);

		// "Latest ref" pattern (same as FormikTextField's onFocusRef/onBlurRef): keeps
		// setUntouchedOnBlur/handleChange referentially stable while still forwarding whichever
		// onBlur/onChange instance a consumer currently passes, instead of silently dropping it.
		const onBlurRef = useRef(onBlur);
		onBlurRef.current = onBlur;
		const onChangeRef = useRef(onChange);
		onChangeRef.current = onChange;

		const setUntouchedOnBlur = useCallback(
			(event: FocusEvent<HTMLSelectElement>) => {
				if (event.target.value === "") {
					helpers.setTouched(false);
				}
				setHasChanged(false);
				if (typeof onBlurRef.current === "function") {
					onBlurRef.current(event);
				}
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[]
		);

		const handleChange = useCallback(
			(event: ChangeEvent<HTMLSelectElement>) => {
				helpers.setValue(event.target.value);
				if (!hasChanged && event.target.value !== "") {
					helpers.setError(undefined);
					setHasChanged(true);
				}
				if (typeof onChangeRef.current === "function") {
					onChangeRef.current(event);
				}
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[hasChanged]
		);

		const stableClassName = makeStableClassName(dataCy);

		const hintId = hint ? `${id}-hint` : undefined;
		// Matches SelectFormik's exact current predicate: gated on touched, unlike
		// InputFormik/FormikTextField's ungated one (see the plan's decision log — this
		// asymmetry is the confirmed root cause of the "error visibility inconsistency").
		const invalid = Boolean(meta.touched && meta.error);

		if (loading) {
			return (
				<div
					className={classNames(styles["formik-select-field"], className)}
					data-testid={dataCy}
				>
					<div className={styles["formik-select-field__loader"]}>
						<HorizontalDotLoader dotSize="3px" color="#3493dc" />
					</div>
				</div>
			);
		}

		return (
			<div
				className={classNames(styles["formik-select-field"], className)}
				data-testid={dataCy}
			>
				<InputCore
					invalid={invalid}
					disabled={disabled}
					data-floated={isLabelFloated || undefined}
					className={classNames(
						styles["formik-select-field__core"],
						stableClassName("formik-select-field__core")
					)}
					trailingSlot={
						<ArrowDownIcon
							aria-hidden="true"
							className={classNames(
								styles["formik-select-field__chevron"],
								stableClassName("formik-select-field__chevron")
							)}
							size={ICON_SIZES.MD}
						/>
					}
				>
					<NativeSelect
						ref={ref}
						id={id}
						disabled={disabled}
						aria-invalid={invalid || undefined}
						aria-required={isRequired || undefined}
						aria-describedby={hintId}
						{...field}
						{...restProps}
						onBlur={setUntouchedOnBlur}
						onChange={handleChange}
						className={classNames(
							styles["formik-select-field__input"],
							stableClassName("formik-select-field__input")
						)}
					>
						{children}
					</NativeSelect>
					<FieldLabel
						htmlFor={id}
						error={invalid}
						required={isRequired}
						data-floated={isLabelFloated || undefined}
						className={classNames(
							styles["formik-select-field__label"],
							stableClassName("formik-select-field__label")
						)}
					>
						{label}
					</FieldLabel>
				</InputCore>
				{hint && (
					<FieldHint id={hintId} variant={invalid ? "error" : "help"}>
						{hint}
					</FieldHint>
				)}
			</div>
		);
	}
);

FormikSelectField.displayName = "FormikSelectField";

export { FormikSelectField };
